// src/components/Header.tsx - UPDATED with marketing dropdown
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { 
  LogOut, 
  User, 
  Settings, 
  ChevronDown,
  Rocket, Flag,
  Crown,
  Building,
  Star,
  Bell,
  Search,
  HelpCircle,
  Menu,
  X,
  Home,
  Settings as SettingsIcon,
  Users,
  FileText,
  Shield,
  Globe,
  Briefcase,
  BookOpen,
  Mail,
  Lock,
  FileCheck,
  Eye
} from 'lucide-react';

interface HeaderProps {
  onNavModeToggle?: () => void;
  currentNavMode?: 'traditional' | 'floating';
  // Full-screen mobile experiences (Global Connect) render their own tab bar
  // and don't have room for a second OS-switcher bar on top of it -- see
  // AppLayout's isImmersiveChatPage.
  hideMobileOSNav?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onNavModeToggle, currentNavMode = 'traditional', hideMobileOSNav = false }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile, isLoading, userTier } = useUserProfile();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMarketingDropdownOpen, setIsMarketingDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Navigation items - All countries in horizontal scroll (same as before)
  // UPRM changed the model: users navigate by OS, not by country.
  // Country selection lives INSIDE the UPRM (dividers) and AssessmentOS.
  const navItems = [
    { path: '/journey',                    label: 'Mission Control', flag: '🚀', active: true },
    { path: '/power-tap',                  label: 'Power Tap',       flag: '⚡', active: true },
    { path: '/wallet',                     label: 'VGC Wallet',      flag: '🪙', active: true },
    { path: '/vgc',                        label: 'Pricing',         flag: '💎', active: true },
    { path: '/course-center',              label: 'Course Center',   flag: '🎓', active: true },
    { path: '/uk-tools-ecosystem',         label: 'UK Tools',        flag: '🛠️', active: true },
    { path: '/canada-tools-ecosystem',     label: 'Canada Tools',    flag: '🇨🇦', active: true },
    { path: '/de-tools-ecosystem',         label: 'Germany Tools',   flag: '🇩🇪', active: true },
    { path: '/australia-tools-ecosystem',  label: 'Australia Tools', flag: '🇦🇺', active: true },
    { path: '/nz-tools-ecosystem',         label: 'NZ Tools',        flag: '🇳🇿', active: true },
    { path: '/ireland-tools-ecosystem',    label: 'Ireland Tools',   flag: '🇮🇪', active: true },
    { path: '/nz-skilled-companion',       label: 'NZ Companion',    flag: '🥝', active: true },
    { path: '/ireland-skills-companion',   label: 'Ireland Companion', flag: '☘️', active: true },
    { path: '/uk-cos-companion',           label: 'COS Companion',   flag: '📋', active: true },
    { path: '/canada-pr-companion',        label: 'Canada PR Companion', flag: '🍁', active: true },
    { path: '/germany-skilled-companion',  label: 'Germany Companion', flag: '🇩🇪', active: true },
    { path: '/opportunity',                label: 'Opportunity',     flag: '💼', active: true },
    { path: '/opportunity/sponsors',       label: 'Sponsors',        flag: '🛂', active: true },
    { path: '/journey-entrance',               label: 'AssessmentOS',    flag: '🎯', active: true },
    { path: '/pathway',                    label: 'PathwayOS',       flag: '🧭', active: true },
    { path: '/documents',                  label: 'DocsOS',          flag: '📄', active: true },
    { path: '/finance',                    label: 'FinanceOS',       flag: '💷', active: true },
    { path: '/journey-compass',            label: 'Compass',         flag: '🧭', active: true },
    { path: '/interview-simulator',        label: 'InterviewOS',     flag: '🎙️', active: true },
    { path: '/visa-form-os',                label: 'Visa Form OS',    flag: '📝', active: true },
    { path: '/language',                   label: 'LanguageOS',      flag: '🌍', active: true },
    { path: '/quantum-appeal',             label: 'Appeal OS',       flag: '⚖️', active: true },
    { path: '/clearid',                    label: 'ClearID',         flag: '🛡️', active: true },
    { path: '/flightos',                   label: 'FlightOS',        flag: '✈️', active: true },
    { path: '/global-connect',             label: 'Global Connect',  flag: '🌐', active: true },
    { path: '/assessment-control-centre',  label: 'Control Centre',  flag: '🎛️', active: true },
    { path: '/agency',                     label: 'Agency OS',       flag: '🏢', active: true },
  ];

  // Merged into the single dropdown below. 'Documents' dropped — DocsOS is already
  // in the OS menu; two routes to one place is how maps drift out of sync.
  const systemMenuItems: any[] = [];

  // Marketing dropdown menu items
  const marketingMenuItems = [
    { label: 'How It Works', path: '/how-it-works', icon: <HelpCircle className="h-4 w-4" /> },
    { label: 'Pricing', path: '/pricing', icon: <Star className="h-4 w-4" /> },
    { label: 'Why Us', path: '/why-us', icon: <Shield className="h-4 w-4" /> },
    { label: 'Team', path: '/team', icon: <Users className="h-4 w-4" /> },
    { divider: true },
    { label: 'About', path: '/about', icon: <Globe className="h-4 w-4" /> },
    { label: 'Blog', path: '/blog', icon: <BookOpen className="h-4 w-4" /> },
    { label: 'Home', path: '/', icon: <Home className="h-4 w-4" /> },
    { label: 'Contact', path: '/contact', icon: <Mail className="h-4 w-4" /> },
    { divider: true },
    { label: 'Privacy Policy', path: '/privacy', icon: <Lock className="h-4 w-4" /> },
    { label: 'Cookie Policy', path: '/cookies', icon: <Eye className="h-4 w-4" /> },
    { label: 'Accessibility', path: '/accessibility', icon: <Eye className="h-4 w-4" /> },
    { divider: true },
    { label: 'Settings', path: '/settings', icon: <SettingsIcon className="h-4 w-4" /> },
    { label: 'Contact / Support', path: '/contact', icon: <HelpCircle className="h-4 w-4" /> },
  ];

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsMarketingDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTierIcon = () => {
    switch (userTier) {
      case 'premium': return <Crown className="h-3 w-3 text-yellow-500" />;
      case 'premium_pro': return <Star className="h-3 w-3 text-purple-500" />;
      case 'agency': return <Building className="h-3 w-3 text-blue-500" />;
      case 'admin': return <Rocket className="h-3 w-3 text-green-500" />;
      default: return null;
    }
  };

  const getTierBadge = () => {
    switch (userTier) {
      case 'journey':     return 'Journey';
      case 'journey_pro': return 'Journey Pro';
      default:            return 'Free';
    }
  };

  return (
    <>
      <header className="w-full bg-white/90 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200/60 dark:border-white/10 shadow-sm relative z-40">
        {/* Container */}
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            
            {/* Left Section - Logo */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100/80 dark:hover:bg-slate-800 transition-colors text-gray-600 dark:text-white/70"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              {/* Logo */}
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 group"
              >
                <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                  <Rocket className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <h1 className="text-base sm:text-lg md:text-xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent hidden xs:block">
                  VisaGuide OS
                </h1>
              </button>
            </div>

            {/* Center Section - Horizontal Scrollable Navigation + Marketing Dropdown */}
            <div className="hidden md:flex items-center gap-1 flex-1 justify-start overflow-x-auto scrollbar-hide">
              {/* Pro Button */}
              <button
                onClick={() => navigate('/vgc')}
                className="group relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:scale-105 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/20 flex-shrink-0"
              >
                <Crown className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{getTierBadge()}</span>
              </button>
              
              {/* NEW: Marketing Dropdown Button */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMarketingDropdownOpen(!isMarketingDropdownOpen);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-gray-100/80 dark:hover:bg-slate-800 border border-white/10 bg-white/5"
                >
                  <Menu className="h-3.5 w-3.5 text-gray-600 dark:text-white/70" />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Menu</span>
                  <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isMarketingDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isMarketingDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setIsMarketingDropdownOpen(false)}
                    />
                    <div className="absolute left-0 top-full mt-2 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-gray-200/60 dark:border-white/10 backdrop-blur-md py-2 z-50 max-h-[70vh] overflow-y-auto">
                      {marketingMenuItems.map((item, idx) => {
                        if ('divider' in item && item.divider) {
                          return <div key={`divider-${idx}`} className="my-1 border-t border-gray-200/60 dark:border-white/10" />;
                        }
                        const { label, path, icon, countryFlag } = item;
                        return (
                          <button
                            key={label}
                            onClick={() => {
                              navigate(path);
                              setIsMarketingDropdownOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50/80 dark:hover:bg-slate-800 transition-colors duration-150"
                          >
                            {countryFlag ? <span className="text-base">{countryFlag}</span> : icon}
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              
              {/* Divider */}
              <div className="w-px h-5 bg-gray-300 dark:bg-gray-700 mx-1 flex-shrink-0" />
              
              {/* Horizontal Scroll Flag Items */}
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => item.active && item.path && navigate(item.path)}
                  className={`group relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all duration-200 flex-shrink-0 ${
                    item.active 
                      ? 'hover:bg-gray-100/80 dark:hover:bg-slate-800 hover:scale-105 cursor-pointer' 
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                  disabled={!item.active}
                  title={!item.active ? 'Coming Soon' : item.label}
                >
                  <span className="text-base" role="img" aria-label={item.label}>{item.flag}</span>
                  <span className={`text-xs font-medium ${
                    item.active 
                      ? 'text-gray-700 dark:text-gray-300' 
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {item.label}
                  </span>
                  {!item.active && <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1 rounded-full ml-0.5">Soon</span>}
                </button>
              ))}
            </div>

            {/* Right Section - Search, System Menu, Notifications, User */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
              
              {/* Search */}
              <div className="relative">
                <Search className="h-4 w-4 text-gray-400 dark:text-white/50 cursor-pointer hover:text-gray-600 dark:hover:text-white/80 transition-colors md:hidden" />
                <div className="hidden md:flex relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-white/50" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-40 lg:w-64 pl-9 pr-3 py-1.5 bg-gray-100/50 dark:bg-slate-800/50 border border-gray-200/60 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    onFocus={() => setIsSearchOpen(true)}
                    onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                  />
                </div>
              </div>

              {/* System menu merged into the single dropdown on the left. */}
              {/* User Profile */}
              {user && (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100/80 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex items-center justify-center w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full shadow-md">
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : profile?.full_name ? (
                        <span className="text-white text-xs font-bold">{getInitials(profile.full_name)}</span>
                      ) : (
                        <User className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <ChevronDown className={`hidden sm:block h-3 w-3 text-gray-400 transition-transform duration-200 ${
                      isProfileDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setIsProfileDropdownOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-gray-200/60 dark:border-white/10 backdrop-blur-md py-2 z-50">
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {profile?.full_name || 'User'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {getTierIcon()}
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 capitalize">
                              {userTier} Tier
                            </span>
                          </div>
                        </div>
                        <div className="py-2">
                          <button
                            onClick={() => {
                              navigate('/user');
                              setIsProfileDropdownOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50/80 dark:hover:bg-slate-800 transition-colors"
                          >
                            <User className="h-4 w-4 text-gray-400" />
                            <span>View Profile</span>
                          </button>
                          {userTier === 'free' && (
                            <button
                              onClick={() => {
                                navigate('/vgc');
                                setIsProfileDropdownOpen(false);
                              }}
                              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50/80 dark:hover:bg-green-900/20 transition-colors"
                            >
                              <Crown className="h-4 w-4" />
                              <span>Upgrade Plan</span>
                            </button>
                          )}
                        </div>
                        <div className="border-t border-gray-100 dark:border-white/10 pt-2">
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50/80 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notifications Panel */}
        {/* Notifications panel removed — it held two hardcoded messages
            ('AI Assistant Ready', 'System Active') that never changed. */}
      </header>

      {/* Mobile Bottom Navigation. The className below uses !fixed, not fixed --
          the project's global index.css has an unlayered ".z-50 { position:
          relative }" rule (same one documented for the marketing-page fixed-
          background bug) that silently overrode this element's Tailwind
          `fixed`, so it was rendering in normal document flow right after
          <header> instead of pinned to the screen bottom -- e.g. showing up
          as a stray icon row at the TOP of Global Connect instead of an
          actual bottom bar. !fixed forces position:fixed with !important,
          which beats that unlayered non-!important override. */}
      {isMobile && !hideMobileOSNav && (
        <div className="!fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-gray-200/60 dark:border-white/10 z-50">
          <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => navigate('/vgc')}
              className="flex flex-col items-center gap-1 px-3 py-1 rounded-lg flex-shrink-0 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/20"
            >
              <Crown className="h-4 w-4 text-amber-500" />
              <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Pro</span>
            </button>
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => item.active && item.path && navigate(item.path)}
                disabled={!item.active}
                className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-all flex-shrink-0 ${
                  item.active ? 'hover:scale-110 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                }`}
                title={!item.active ? 'Coming Soon' : item.label}
              >
                <span className="text-xl">{item.flag}</span>
                <span className={`text-[10px] font-medium ${
                  item.active ? 'text-gray-600 dark:text-white/80' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {item.label}
                  {!item.active && <span className="ml-0.5 text-[8px]">(Soon)</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile System Menu Drawer (unchanged) */}
      {isMobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300">
            <div className="p-4 border-b border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Rocket className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 dark:text-white">System</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Settings & Tools</p>
                </div>
              </div>
            </div>
            <div className="py-2">
              {systemMenuItems.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {item.icon}
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
              <div className="border-t border-gray-200 dark:border-white/10 my-2"></div>
              <button
                onClick={() => {
                  navigate('/settings');
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <SettingsIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Settings</span>
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </>
  );
};

export default Header;